import { SubmissionError } from "redux-form";

import { Button, Stack } from "@braintrust/braintrust-ui-components";
import { fetchCurrentUser } from "@js/apps/auth/actions";
import {
  useDeletePasskeyMutation,
  useGetPasskeysQuery,
} from "@js/apps/auth/api";
import {
  otpValuesToConfirmation,
  submitActionAfterOTPConfirmation,
} from "@js/apps/common/forms/fields/otp/otp-auth-modal";
import { useUser } from "@js/apps/common/hooks/use-user";
import { SectionSubtitle } from "@js/apps/settings/forms/helpers";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch } from "@js/hooks";

import { PasskeyList } from "../passkey-list";
import { openPasswordConfirmationModal } from "../password-confirmation-modal";
import { useRegisterPasskey } from "../use-register-passkey";

export const PasskeySection = () => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const { data: passkeys = [] } = useGetPasskeysQuery();
  const [deletePasskey] = useDeletePasskeyMutation();
  const { register, isRegistering } = useRegisterPasskey();

  const handleAdd = () => {
    if (user?.is_otp_enabled || user?.is_passkey_enabled) {
      submitActionAfterOTPConfirmation({
        onSubmit: async (values) => {
          try {
            await register(otpValuesToConfirmation(values));
          } catch (error) {
            throw new SubmissionError((error as Record<string, object>)?.data);
          }
          Snackbar.success("Passkey added.");
        },
      });
      return;
    }

    openPasswordConfirmationModal({
      title: "Add passkey",
      description: "Enter your current password to add a new passkey.",
      confirmText: "Add",
      onConfirm: async (currentPassword) => {
        await register({ current_password: currentPassword });
        Snackbar.success("Passkey added.");
      },
    });
  };

  const handleDelete = (id: number) => {
    if (user?.is_otp_enabled || user?.is_passkey_enabled) {
      submitActionAfterOTPConfirmation({
        onSubmit: async (values) => {
          try {
            await deletePasskey({
              id,
              confirmation: otpValuesToConfirmation(values),
            }).unwrap();
          } catch (error) {
            throw new SubmissionError((error as Record<string, object>)?.data);
          }
          await dispatch(fetchCurrentUser());
          Snackbar.success("Passkey removed.");
        },
      });
      return;
    }

    openPasswordConfirmationModal({
      title: "Remove passkey",
      description: "Enter your current password to remove this passkey.",
      confirmText: "Remove",
      onConfirm: async (currentPassword) => {
        await deletePasskey({
          id,
          confirmation: { current_password: currentPassword },
        }).unwrap();
        void dispatch(fetchCurrentUser());
        Snackbar.success("Passkey removed.");
      },
    });
  };

  return (
    <Stack>
      <SectionSubtitle
        title="Passkeys"
        description="Add a passkey as an optional 2-step verification method when signing in."
        descriptionContainerProps={{ sx: { mb: 3 } }}
        action={
          <Button
            variant="secondary"
            onClick={handleAdd}
            disabled={isRegistering}
          >
            {isRegistering ? "Adding…" : "Add passkey"}
          </Button>
        }
      />

      <PasskeyList passkeys={passkeys} onDelete={handleDelete} />
    </Stack>
  );
};
